import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TeamCsvImport } from "@/components/TeamCsvImport";
import { TeamGradebook } from "@/components/TeamGradebook";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { BrandLogo } from "@/components/BrandLogo";
import {
  UserPlus, Trash2, Loader2, ArrowLeft, GraduationCap, ShieldCheck, BarChart3, FileDown,
} from "lucide-react";

export default function InstructorTeam() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [teams, setTeams] = useState(null);
  const [email, setEmail] = useState({});
  const [openGb, setOpenGb] = useState(null);

  const load = () => api.get("/api/team/mine").then(({ data }) => setTeams(data.teams)).catch(() => setTeams([]));
  useEffect(() => { load(); }, []);

  const err = (e, fb) => toast.error(formatApiErrorDetail(e?.response?.data?.detail) || fb);

  const addMember = async (t) => {
    const e = (email[t.id] || "").trim();
    if (!e) return;
    try {
      const { data } = await api.post(`/api/team/${t.id}/members`, { email: e });
      toast.success(`Added ${e}.`);
      setEmail((m) => ({ ...m, [t.id]: "" }));
      setTeams((ts) => ts.map((x) => (x.id === t.id ? data : x)));
    } catch (e2) { err(e2, "Couldn't add member."); }
  };

  const removeMember = async (t, uid) => {
    try {
      const { data } = await api.delete(`/api/team/${t.id}/members/${uid}`);
      setTeams((ts) => ts.map((x) => (x.id === t.id ? data : x)));
    } catch (e2) { err(e2, "Couldn't remove member."); }
  };

  const onImported = (teamId, updated) =>
    setTeams((ts) => ts.map((x) => (x.id === teamId ? updated : x)));

  return (
    <div className="min-h-screen bg-[#F4F7F9]" data-testid="instructor-team-page">
      <header className="border-b border-slate-200 bg-white/70 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/sections" className="flex items-center gap-3" data-testid="brand-logo-home">
            <BrandLogo chip />
            <span className="hidden sm:block text-[11px] text-slate-500 border-l border-slate-200 pl-3">Instructor area</span>
          </Link>
          <div className="flex items-center gap-2">
            <a
              href="/CertIVSims-Instructor-Guide.pdf"
              download
              data-testid="download-instructor-guide-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-[#0047AB]/30 text-[#0047AB] text-xs font-semibold hover:bg-[#0047AB]/5 transition-colors whitespace-nowrap"
            >
              <FileDown className="w-4 h-4" />
              <span className="hidden sm:inline">Instructor guide</span>
              <span className="sm:hidden">Guide</span>
            </a>
            <Button variant="outline" size="sm" onClick={() => navigate("/sections")} className="text-slate-600">
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to hub
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10 fade-up">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#0047AB] font-semibold mb-2">
          <ShieldCheck className="w-3.5 h-3.5" /> <span>Instructor</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">My team</h1>
        <p className="text-base text-slate-600 mt-2">
          Add learners individually or import a class roster as a CSV. Open the gradebook to see each learner's scores, competency and completion.
        </p>

        <div className="mt-8 space-y-5">
          {teams === null ? (
            <div className="flex items-center gap-2 text-slate-400 text-sm py-16 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : teams.length === 0 ? (
            <Card className="border-dashed border-2 border-slate-200 p-12 text-center" data-testid="no-team">
              <GraduationCap className="w-8 h-8 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600 text-sm">
                You don't manage a team yet. An administrator can assign you as the instructor of a team.
              </p>
            </Card>
          ) : (
            teams.map((t) => {
              const seatsLeft = Math.max(0, (t.seats || 0) - t.member_count);
              const gbOpen = openGb === t.id;
              return (
                <Card key={t.id} className="border-slate-200 p-5" data-testid={`my-team-${t.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <h2 className="text-lg font-bold text-slate-900">{t.name}</h2>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${t.active ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-amber-300 bg-amber-50 text-amber-700"}`}>
                          {t.active ? "ACTIVE" : "PAYMENT PENDING"}
                        </span>
                      </div>
                      <p className="text-sm text-slate-500 mt-0.5">
                        {t.member_count}/{t.seats} seats used · {seatsLeft} available
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" onClick={() => setOpenGb(gbOpen ? null : t.id)}
                        data-testid={`team-gradebook-toggle-${t.id}`}
                        className={`text-xs ${gbOpen ? "bg-[#0047AB] text-white border-[#0047AB] hover:bg-[#003380]" : "text-[#0047AB] border-[#0047AB]/30"}`}>
                        <BarChart3 className="w-3.5 h-3.5 mr-1" /> Gradebook
                      </Button>
                      <TeamCsvImport
                        endpoint={`/api/team/${t.id}/members/bulk`}
                        teamName={t.name}
                        seatsLeft={seatsLeft}
                        testid={`team-${t.id}-csv`}
                        onImported={(updated) => onImported(t.id, updated)}
                      />
                    </div>
                  </div>

                  {gbOpen && (
                    <div className="mt-4 border-t border-slate-100 pt-4" data-testid={`team-gradebook-panel-${t.id}`}>
                      <TeamGradebook teamId={t.id} teamName={t.name} />
                    </div>
                  )}

                  <div className="mt-4 border-t border-slate-100 pt-4">
                    <div className="flex flex-wrap gap-2 mb-3">
                      {t.members.length === 0 ? (
                        <span className="text-xs text-slate-400">No learners added yet.</span>
                      ) : t.members.map((m) => (
                        <span key={m.id} className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-700 text-xs rounded-full pl-3 pr-1.5 py-1" data-testid={`my-team-member-${m.id}`}>
                          {m.email}
                          {!t.manager_ids.includes(m.id) && (
                            <button onClick={() => removeMember(t, m.id)} className="text-slate-400 hover:text-rose-500" data-testid={`my-team-member-remove-${m.id}`}>
                              <Trash2 className="w-3 h-3" />
                            </button>
                          )}
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Input value={email[t.id] || ""} onChange={(e) => setEmail((mm) => ({ ...mm, [t.id]: e.target.value }))}
                        placeholder="learner@email.com" data-testid={`my-team-member-input-${t.id}`}
                        className="h-9 text-sm max-w-xs" disabled={seatsLeft <= 0} />
                      <Button size="sm" onClick={() => addMember(t)} disabled={seatsLeft <= 0}
                        data-testid={`my-team-add-member-${t.id}`} className="bg-[#0047AB] hover:bg-[#003380] text-white">
                        <UserPlus className="w-3.5 h-3.5 mr-1" /> Add
                      </Button>
                    </div>
                    {seatsLeft <= 0 && <p className="text-xs text-amber-600 mt-2">All seats are in use. Ask an admin to add seats.</p>}
                  </div>
                </Card>
              );
            })
          )}
        </div>
      </main>
    </div>
  );
}
